import { uiText, useUiLanguage } from '@/i18n/interface';
import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Users,
  Calendar,
  Trophy,
  Star,
  ChevronRight,
  Quote,
} from "lucide-react";
import warriorHubLogo from "@/assets/warriorhub-logo.png";

const Index = () => {
  useUiLanguage();
  const { isAuthenticated, profile, isLoading } = useAuth();
  const navigate = useNavigate();

  // Check if user came here intending to login/signup
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const intent = urlParams.get("intent");

    if (intent === "login" || intent === "signup") {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // Show loading state while auth is being determined
  if (isLoading) {
    return (
      <div className="wh-app dark min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
          <p className="mt-2 text-sm text-white">{uiText("Loading...")}</p>
        </div>
      </div>
    );
  }

  // If user is authenticated, redirect to their dashboard
  if (isAuthenticated && profile) {
    if (profile.role === "student") {
      return <Navigate to="/student" replace />;
    } else if (profile.role === "admin") {
      return <Navigate to="/admin" replace />;
    } else if (profile.role === "super_user") {
      return <Navigate to="/super-user" replace />;
    }
  }

  const reviews = [
    {
      name: "Sensei Marcus Thompson",
      dojo: "Elite Combat Academy",
      rating: 5,
      review:
        "WarriorHub completely transformed how we manage our dojo. The attendance tracking and belt progression features are game-changers. Our students love the digital experience!",
      avatar: "M",
    },
    {
      name: "Master Sarah Chen",
      dojo: "Dragon Mountain Martial Arts",
      rating: 5,
      review:
        "The payment management system alone saved us hours each week. The professional reports and analytics help us make better decisions for our school.",
      avatar: "S",
    },
    {
      name: "Instructor David Rodriguez",
      dojo: "Phoenix Karate Center",
      rating: 5,
      review:
        "Our students are more engaged than ever with the on-demand training videos and progress tracking. WarriorHub brought our traditional dojo into the digital age.",
      avatar: "D",
    },
  ];

  const stats = [
    { number: "500+", label: uiText("Martial Arts Schools") },
    { number: "25,000+", label: uiText("Active Students") },
    { number: "1M+", label: uiText("Training Sessions Tracked") },
    { number: "99.9%", label: uiText("Uptime Reliability") },
  ];

  return (
    <div className="wh-app dark min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20" />

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-blue-500/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-purple-500/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-cyan-500/10 rounded-full blur-xl animate-pulse delay-500"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="text-center">
            {/* Logo Section */}
            <div className="flex justify-center mb-8">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full opacity-20 group-hover:opacity-30 transition-opacity blur-xl"></div>
                <img
                  src={warriorHubLogo}
                  alt={uiText("WarriorHub Logo")}
                  className="relative w-24 h-24 md:w-32 md:h-32 object-contain drop-shadow-2xl group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>

            <div className="flex justify-center mb-8">
              <Badge
                variant="secondary"
                className="bg-blue-100/90 text-blue-800 px-6 py-3 text-sm backdrop-blur-sm border border-blue-200/50"
              >
                <Shield className="w-4 h-4 mr-2" />
                {uiText("#1 Professional Martial Arts Management Platform")}</Badge>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
              {uiText("Welcome to")}{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 animate-gradient">
                {uiText("WarriorHub")}</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              {uiText("Created by a karate teacher who understands the real challenges of running a martial arts academy. Unlike generic software, WarriorHub was built from the ground up to solve actual dojo problems.")}</p>

            <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 backdrop-blur-sm rounded-2xl p-6 mb-12 max-w-4xl mx-auto border border-blue-400/20">
              <p className="text-lg md:text-xl text-blue-100 leading-relaxed">
                <span className="text-yellow-400 font-semibold">
                  {uiText("💡 Born from Experience:")}</span>{" "}
                {uiText("Get full access to professional-grade features at a fraction of the cost. While other platforms charge double for basic functionality, WarriorHub delivers everything your dojo needs in one comprehensive, reasonably-priced solution. Trial it risk-free for 14 days and see the difference for yourself.")}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
              <Button
                size="lg"
                onClick={() => navigate("/register")}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-10 py-4 text-lg font-semibold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105"
              >
                {uiText("Start Your Free Trial")}<ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/login")}
                className="border-2 border-white/30 text-white hover:bg-white hover:text-slate-900 px-10 py-4 text-lg font-semibold backdrop-blur-sm transition-all duration-300 hover:scale-105"
              >
                {uiText("Sign In")}</Button>
            </div>

            {/* Stats Section */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-400 text-sm md:text-base">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-white/5 backdrop-blur-sm relative">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-purple-900/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {uiText("Built by Martial Artists, for Martial Artists")}</h2>
            <p className="text-gray-300 text-xl max-w-4xl mx-auto leading-relaxed mb-8">
              {uiText("Unlike generic business software adapted for dojos, WarriorHub was designed from day one by someone who lives and breathes martial arts. Every feature addresses real problems that instructors face daily.")}</p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge
                variant="secondary"
                className="bg-yellow-100/90 text-yellow-800 px-4 py-2 text-base"
              >
                {uiText("✅ Complete Solution - One Platform")}</Badge>
              <Badge
                variant="secondary"
                className="bg-green-100/90 text-green-800 px-4 py-2 text-base"
              >
                {uiText("💰 Affordable Pricing - No Hidden Costs")}</Badge>
              <Badge
                variant="secondary"
                className="bg-blue-100/90 text-blue-800 px-4 py-2 text-base"
              >
                {uiText("🥋 Made by Martial Artists")}</Badge>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/15 transition-all duration-300 group hover:scale-105">
              <CardHeader>
                <Users className="w-10 h-10 text-blue-400 mb-4 group-hover:scale-110 transition-transform" />
                <CardTitle className="text-xl">{uiText("Student Management")}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 leading-relaxed">
                  {uiText("Comprehensive student profiles, attendance tracking, and progress monitoring with detailed analytics.")}</CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/15 transition-all duration-300 group hover:scale-105">
              <CardHeader>
                <Trophy className="w-10 h-10 text-yellow-400 mb-4 group-hover:scale-110 transition-transform" />
                <CardTitle className="text-xl">{uiText("Belt Progression")}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 leading-relaxed">
                  {uiText("Digital belt requirements, progress tracking, and automated promotion management system.")}</CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/15 transition-all duration-300 group hover:scale-105">
              <CardHeader>
                <Calendar className="w-10 h-10 text-green-400 mb-4 group-hover:scale-110 transition-transform" />
                <CardTitle className="text-xl">{uiText("Schedule Management")}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 leading-relaxed">
                  {uiText("Easy class scheduling, session management, and automated notifications for students and instructors.")}</CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/15 transition-all duration-300 group hover:scale-105">
              <CardHeader>
                <Star className="w-10 h-10 text-purple-400 mb-4 group-hover:scale-110 transition-transform" />
                <CardTitle className="text-xl">{uiText("Payment Tracking")}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 leading-relaxed">
                  {uiText("Streamlined payment management, automated invoicing, and comprehensive financial reporting.")}</CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Value Proposition Section */}
      <div className="py-24 bg-gradient-to-r from-green-900/20 to-emerald-900/20 relative">
        <div className="absolute inset-0 backdrop-blur-sm"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {uiText("Why Pay Double for Half the Features?")}</h2>
            <p className="text-gray-300 text-xl max-w-3xl mx-auto">
              {uiText("Compare WarriorHub's complete solution with typical \"martial arts software\" that charges premium prices for basic features.")}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <Card className="bg-red-900/20 backdrop-blur-sm border-red-400/30 text-white">
              <CardHeader>
                <CardTitle className="text-2xl text-red-300">
                  {uiText("❌ Other Platforms")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-gray-300">
                  <p className="mb-2">{uiText("• Basic student management only")}</p>
                  <p className="mb-2">{uiText("• Limited payment tracking")}</p>
                  <p className="mb-2">{uiText("• No belt progression system")}</p>
                  <p className="mb-2">{uiText("• Extra fees for \"premium\" features")}</p>
                  <p className="mb-2">
                    {uiText("• Generic design, not martial arts focused")}</p>
                  <p className="mb-4">{uiText("• Poor customer support")}</p>
                  <div className="text-red-300 font-bold text-xl">
                    {uiText("💸 €100-200+ per month for basic features")}</div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-green-900/20 backdrop-blur-sm border-green-400/30 text-white">
              <CardHeader>
                <CardTitle className="text-2xl text-green-300">
                  {uiText("✅ WarriorHub")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-gray-300">
                  <p className="mb-2">{uiText("• Complete dojo management suite")}</p>
                  <p className="mb-2">{uiText("• Advanced payment & billing system")}</p>
                  <p className="mb-2">{uiText("• Smart belt progression tracking")}</p>
                  <p className="mb-2">
                    {uiText("• All features included, no hidden costs")}</p>
                  <p className="mb-2">{uiText("• Built specifically for martial arts")}</p>
                  <p className="mb-4">
                    {uiText("• Support from martial artists who understand")}</p>
                  <div className="text-green-300 font-bold text-xl">
                    {uiText("🎯 €49.99/month - Complete professional solution")}</div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-2xl font-bold text-yellow-400 mb-4">
              {uiText("Created by a Karate Teacher Who Solved His Own Problems")}</p>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              {uiText("Every feature in WarriorHub exists because it solved a real problem in a real dojo. This isn't software made by developers who've never stepped on a mat – it's built by someone who knows exactly what you need.")}</p>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {uiText("Trusted by Martial Arts Leaders")}</h2>
            <p className="text-gray-300 text-xl max-w-3xl mx-auto">
              {uiText("See what instructors and dojo owners are saying about WarriorHub")}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <Card
                key={index}
                className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/15 transition-all duration-300 hover:scale-105"
              >
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {review.avatar}
                    </div>
                    <div>
                      <div className="font-semibold">{review.name}</div>
                      <div className="text-sm text-gray-400">{review.dojo}</div>
                    </div>
                  </div>
                  <div className="flex gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <Quote className="w-8 h-8 text-blue-400/50 absolute -top-2 -left-2" />
                    <p className="text-gray-300 leading-relaxed pl-6">
                      {review.review}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 bg-gradient-to-r from-blue-900/30 to-purple-900/30 relative">
        <div className="absolute inset-0 backdrop-blur-sm"></div>
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            {uiText("Ready to Transform Your Dojo?")}</h2>
          <p className="text-gray-300 text-xl mb-12 leading-relaxed">
            {uiText("Join hundreds of martial arts schools already using WarriorHub to streamline their operations and enhance their students' experience.")}</p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button
              size="lg"
              onClick={() => navigate("/register")}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-10 py-4 text-lg font-semibold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105"
            >
              {uiText("Start Your Free Trial")}<ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/login")}
              className="border-2 border-white/30 text-white hover:bg-white hover:text-slate-900 px-10 py-4 text-lg font-semibold backdrop-blur-sm transition-all duration-300 hover:scale-105"
            >
              {uiText("Sign In")}</Button>
          </div>
        </div>
      </div>
      {/* Powered by Supabase Section */}
      <div className="py-16 bg-black/30 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-6">
            <img
              src="https://avatars.githubusercontent.com/u/54469796?s=200&v=4"
              alt={uiText("Supabase Icon")}
              className="w-12 h-12 mx-auto mb-4"
            />
          </div>
          <h3 className="text-xl md:text-2xl font-semibold text-white mb-2">
            {uiText("Powered by Supabase")}</h3>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            {uiText("WarriorHub is built on Supabase — an open-source, scalable, and secure backend infrastructure. Real-time, cloud-native, and blazing fast.")}</p>
        </div>
      </div>
      {/* Footer */}
      <div className="border-t border-white/10 py-12 bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <img
                src={warriorHubLogo}
                alt={uiText("WarriorHub Logo")}
                className="w-12 h-12 object-contain opacity-80"
              />
            </div>
            <p className="text-gray-400 text-lg mb-4">
              {uiText("© 2024 WarriorHub. Professional martial arts management platform.")}</p>
            <p className="text-gray-500 text-sm">
              {uiText("Empowering martial arts communities worldwide with cutting-edge technology.")}</p>

            {/* Contact Section */}
            <section
              id="contact"
              className="bg-black/30 border-t border-white/10 py-16 px-6"
            >
              <div className="max-w-3xl mx-auto text-center text-white">
                <h2 className="text-3xl font-semibold mb-4">{uiText("Contact Us")}</h2>
                <p className="text-gray-300 mb-6">
                  {uiText("Have questions, need support, or want to give feedback? We’d love to hear from you.")}</p>
                <div className="flex flex-col gap-2 items-center">
                  <p className="text-gray-400">
                    {uiText("📧 Email:")}{" "}
                    <a
                      href="mailto:toubikleo@gmail.com"
                      className="text-blue-400 hover:underline"
                    >
                      toubikleo@gmail.com
                    </a>
                  </p>
                  <p className="text-gray-400">
                    {uiText("🌐 Website:")}{" "}
                    <a
                      href="https://warrior-hub.vercel.app "
                      className="text-blue-400 hover:underline"
                    >
                      https://warrior-hub.vercel.app
                    </a>
                  </p>
                </div>
              </div>
            </section>

            {/* Disclaimer Section */}
            <section
              id="disclaimer"
              className="bg-red-900/20 border-t border-white/10 py-16 px-6 text-white text-center"
            >
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold mb-4">{uiText("Disclaimer")}</h2>
                <p className="text-gray-300 mb-4">
                  {uiText("WarriorHub is a software-as-a-service platform provided \"as is\", with no guarantees of availability, performance, or results. The developer assumes no liability for financial, legal, or data-related issues that may arise from using this platform.")}</p>
                <p className="text-gray-400 text-sm">
                  {uiText("By using WarriorHub, you accept full responsibility for any and all usage, data entered, and actions taken within the platform. Always consult your legal advisor if you are operating in a regulated environment.")}</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
